import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { useAutoCheckOffTask } from 'src/app/pages/starterDashboardV2/shared/hooks';
import { useAvailableShareActions } from 'src/app/shared/hooks/share';
import type { ShareActionsOptions } from 'src/app/shared/hooks/share';
import { copyToClipboard } from 'src/app/shared/utils/clipboard';

import { useShareUrlWithUtmParams } from '../../utils/getShareUrlWithUtmParams';

import type { ActionsReturnType, PostSharingHookProps } from './types';

export function useFacebookPostSharing(props: PostSharingHookProps): ActionsReturnType {
	const i18n = useI18n();
	const autoCheckOffTask = useAutoCheckOffTask();

	const {
		message,
		trackingProps: { context, viewContext },
		shareUrl = '',
	} = props;

	const shareUrlWithUtmParams = useShareUrlWithUtmParams(shareUrl, 'facebook_post');

	const options: ShareActionsOptions<'facebook'> = {
		trackingEventName: 'share_petition',
		getUrl: (_channel) => shareUrlWithUtmParams || shareUrl,
		getContent: (_channel, _type, _ctx) => '',
		getTrackingData: (_channel) => {
			return {
				context,
				location: 'facebook_post',
				tab_at_share: viewContext,
				app_available: false,
				content: 'post',
			};
		},
		onSuccess: (_channel) => {
			// noop required by api
		},
	};

	const shareActions = useAvailableShareActions(['facebook'], options);
	const primaryAction = useCallback(async () => {
		void copyToClipboard(message, i18n);

		if (shareActions.length > 0) {
			await shareActions[0].action();
		}
		await autoCheckOffTask('share_facebook');
	}, [i18n, message, shareActions, autoCheckOffTask]);

	return {
		primaryAction,
		secondaryAction: undefined,
	};
}
